import React, { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, Button, Text, useColorModeValue, Heading, Stack, Icon } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaBook } from "react-icons/fa";
import { StudentLessonNode } from "../Common/types";
import { useTenant } from "../Routers/TenantContextProvider";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

interface LocationState {
  lesson: StudentLessonNode;
  batchTitle?: string;
  termTitle?: string;
}

const StudentLessonViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const state = location.state as LocationState | undefined;
  const lesson = state?.lesson;

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [activeView, setActiveView] = useState<"summary" | "content" | "assignment" | null>(null);
  const [loadingView, setLoadingView] = useState<"summary" | "content" | "assignment" | null>(null);

  const [containerWidth, setContainerWidth] = useState<number>(window.innerWidth * 0.9);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const bg = useColorModeValue("gray.100", "gray.700");
  const dividerBg = useColorModeValue("gray.100", "gray.700");

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => {
      window.removeEventListener("resize", updateWidth);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // Reset the viewer whenever a different lesson is selected from the sidebar.
// Same route, only router `state` changes, so the component doesn't remount —
// without this, the previous lesson's PDF stays visible until a view button
// is explicitly clicked again.
useEffect(() => {
  setPdfUrl((prevUrl) => {
    if (prevUrl) URL.revokeObjectURL(prevUrl);
    return null;
  });
  setPdfTitle(null);
  setActiveView(null);
  setLoadingView(null);
  setNumPages(null);
}, [lesson?.id]);

  if (!lesson) {
    return (
      <Box p={6}>
        <Text>No lesson selected. Please go back and pick a lesson from your batches.</Text>
        {tenant && (
          <Button mt={4} onClick={() => navigate(`/platform/${tenant.tenant_slug}/app/student-batches`)}>
            Back to My Batches
          </Button>
        )}
      </Box>
    );
  }

  const hasSummary = !!lesson.summary_url;
  const hasContent = !!lesson.content_url;
 // const hasAssignment = !!lesson.assignment_url;

  const handleViewSummary = async () => {
    if (!hasSummary) return;
    setActiveView("summary");
    setLoadingView("summary");
    setPdfTitle(`Summary — ${lesson.title}`);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "https://default-url.com";
      const response = await fetch(`${apiUrl}/stream-summary-content/${lesson.id}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok) throw new Error("Failed to fetch summary PDF");
      const pdfBlob = await response.blob();
      setPdfUrl(URL.createObjectURL(pdfBlob));
    } catch (error) {
      console.error("Error fetching summary PDF:", error);
    } finally {
      setLoadingView(null);
    }
  };

   /*  const handleViewAssignment = async () => {
    if (!hasAssignment) return;
    setActiveView("assignment");
    setLoadingView("assignment");
    setPdfTitle(`Assignment — ${lesson.title}`);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "https://default-url.com";
      const response = await fetch(`${apiUrl}/stream-assignment-content/${lesson.id}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok) throw new Error("Failed to fetch assignment PDF");
      const pdfBlob = await response.blob();
      setPdfUrl(URL.createObjectURL(pdfBlob));
    } catch (error) {
      console.error("Error fetching assignment PDF:", error);
    } finally {
      setLoadingView(null);
    }
  }; */

  const handleViewContent = async () => {
    if (!hasContent) return;
    setActiveView("content");
    setLoadingView("content");
    setPdfTitle(`Lesson — ${lesson.title}`);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "https://default-url.com";
      const response = await fetch(`${apiUrl}/stream-full-content/${lesson.id}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok) throw new Error("Failed to fetch lesson content PDF");
      const pdfBlob = await response.blob();
      setPdfUrl(URL.createObjectURL(pdfBlob));
    } catch (error) {
      console.error("Error fetching content PDF:", error);
    } finally {
      setLoadingView(null);
    }
  };

  return (
    <Box width="100%" bg={bg} minH="100vh" p={2}>
      <Box p={4} bg="white" borderRadius="lg" boxShadow="xl" borderWidth="1px" borderColor="gray.200" maxW="1200px" mx="auto">
        {(state?.batchTitle || state?.termTitle) && (
          <Text fontSize="sm" color="gray.500" mb={1}>
            {state?.batchTitle} {state?.termTitle ? `• ${state.termTitle}` : ""}
          </Text>
        )}

        <Heading size="lg" mb={2} color="teal.600">
          <Icon as={FaBook} color="purple.500" mr={2} />
          {lesson.title}
        </Heading>

        {lesson.description && (
          <Text fontSize="sm" color="gray.600" mb={4}>
            {lesson.description}
          </Text>
        )}

        <Stack direction={{ base: "column", sm: "row" }} spacing={3}>
          <Button
            colorScheme={activeView === "summary" ? "blue" : "gray"}
            variant={activeView === "summary" ? "solid" : "outline"}
            onClick={handleViewSummary}
            isLoading={loadingView === "summary"}
            isDisabled={!hasSummary}
          >
            {hasSummary ? "View Summary" : "Summary Not Available"}
          </Button>
{/*           <Button
            colorScheme={activeView === "assignment" ? "blue" : "gray"}
            variant={activeView === "assignment" ? "solid" : "outline"}
            onClick={handleViewAssignment}
            isLoading={loadingView === "assignment"}
            isDisabled={!hasAssignment}
          >
            {hasAssignment ? "View Assignment" : "Assignment Not Available"}
          </Button> */}
          <Button
            colorScheme={activeView === "content" ? "green" : "gray"}
            variant={activeView === "content" ? "solid" : "outline"}
            onClick={handleViewContent}
            isLoading={loadingView === "content"}
            isDisabled={!hasContent}
          >
            {hasContent ? "View Lesson" : "Lesson Not Available"}
          </Button>
        </Stack>
      </Box>

      {pdfUrl && (
        <Box mt={2} ref={containerRef} maxW="1200px" mx="auto" p={4} bg={bg} borderRadius="lg" boxShadow="md">
          <Text fontWeight="bold" mb={2}>{pdfTitle}</Text>
          <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages || 0)}>
            {Array.from(new Array(numPages ?? 0), (_, index) => (
              <Box key={index} p={4} bg={dividerBg} display="flex" justifyContent="center">
                <Page pageNumber={index + 1} width={containerWidth - 32} renderTextLayer={false} renderAnnotationLayer={false} />
              </Box>
            ))}
          </Document>
        </Box>
      )}
    </Box>
  );
};

export default StudentLessonViewer;